import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Download, Loader2, X } from 'lucide-react';
import { startBackgroundExport } from '@/utils/organization/backgroundExport';

interface UnifiedExportManagerProps {
  organisation: any;
  isOpen: boolean;
  onClose: () => void;

  // Context
  context: 'inventory' | 'reports';

  // For inventory context
  inventoryActiveTab?: 'table' | 'trends';
  inventoryFilters?: {
    sources: string[];
    dateRange: string;
    customStartDate: Date;
    customEndDate: Date;
    selectedSkus?: string[];
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    multiColumnFilters?: Array<{ column: string; value: string; operation: string }>;
  };

  // For reports context
  reportsActiveTab?: string;
  reportsFilters?: any;
}

export const UnifiedExportManager: React.FC<UnifiedExportManagerProps> = ({
  organisation,
  isOpen,
  onClose,
  context,
  inventoryActiveTab,
  inventoryFilters,
  reportsActiveTab,
  reportsFilters
}) => {
  const [isExporting, setIsExporting] = useState(false);

  // Get initial data source based on inventory_source_config
  const getInitialDataSource = () => {
    const primarySourceConfig = organisation?.inventory_source_config?.primary_source;
    const hasSourceFilter = context === 'inventory' && primarySourceConfig && primarySourceConfig !== 'all';

    if (hasSourceFilter && organisation?.connections && Array.isArray(organisation.connections)) {
      // Find the first connection matching the configured source
      const lowerConfig = primarySourceConfig.toLowerCase();
      const matchingConnection = organisation.connections.find((conn: any) => {
        const type = conn.type?.toLowerCase() || '';
        const service = conn.service?.toLowerCase() || '';
        const isCsvImport = (service === 'csv' || type === 'csv') && conn.import_type === 'ecommerce';

        if (lowerConfig === 'shopify') return type.includes('shopify') && !isCsvImport;
        if (lowerConfig === 'amazon') return type.includes('amazon') && !isCsvImport;
        if (lowerConfig === 'shopify_csv') return isCsvImport && conn.ecommerce_subtype?.toLowerCase() === 'shopify';
        if (lowerConfig === 'amazon_csv') return isCsvImport && conn.ecommerce_subtype?.toLowerCase() === 'amazon';
        return false;
      });

      if (matchingConnection) {
        return matchingConnection.id || matchingConnection.connectionId;
      }
    }
    return 'all';
  };

  const [exportSettings, setExportSettings] = useState({
    dataSource: getInitialDataSource(),
    format: 'csv',
    includeHeaders: true,
    filename: `export_${inventoryActiveTab}_${new Date().toISOString().split('T')[0]}.csv`
  });

  // Get display title based on context
  const getExportTitle = () => {
    if (context === 'inventory') {
      return inventoryActiveTab === 'trends' ? 'Export Trends Over Time' : 'Export Current Stock';
    } else {
      if (reportsActiveTab === 'skus') return 'Export ∑: Products';
      if (reportsActiveTab === 'fixed-costs') return 'Export Fixed Costs';
      if (reportsActiveTab === 'sales-history') return 'Export Sales History';
    }
    return 'Export Report';
  };

  // Get available data sources - filtered by inventory_source_config when applicable
  const getAvailableDataSources = () => {
    const sources: Array<{ id: string; name: string; sourceType: string }> = [];

    // Check if there's a primary source filter configured
    const primarySourceConfig = organisation?.inventory_source_config?.primary_source;
    const hasSourceFilter = context === 'inventory' && primarySourceConfig && primarySourceConfig !== 'all';

    // Only add "All Sources" option if no specific source is configured
    if (!hasSourceFilter) {
      sources.push({ id: 'all', name: 'All Sources', sourceType: 'all' });
    }

    const supportedConnectors = ['shopify', 'amazon'];

    if (organisation?.connections && Array.isArray(organisation.connections)) {
      const connections = organisation.connections;
      const supportedConnections = connections
        .filter((connection: any) => {
          const type = connection.type?.toLowerCase() || '';
          const service = connection.service?.toLowerCase() || '';

          // Determine connection type and if it's a CSV import
          let connectionType = '';
          let isCsvImport = false;

          if (type.includes('shopify')) {
            connectionType = 'shopify';
          } else if (type.includes('amazon')) {
            connectionType = 'amazon';
          } else if ((service === 'csv' || type === 'csv') &&
            connection.import_type === 'ecommerce' &&
            connection.ecommerce_subtype) {
            const subtype = connection.ecommerce_subtype.toLowerCase();
            isCsvImport = true;
            if (subtype === 'shopify') connectionType = 'shopify';
            else if (subtype === 'amazon') connectionType = 'amazon';
          }

          // Skip unsupported connections
          if (!connectionType || !supportedConnectors.includes(connectionType)) {
            return false;
          }

          // Apply source filter if configured (only for inventory context)
          if (hasSourceFilter) {
            const lowerConfig = primarySourceConfig.toLowerCase();

            if (lowerConfig === 'shopify') {
              return connectionType === 'shopify' && !isCsvImport;
            } else if (lowerConfig === 'amazon') {
              return connectionType === 'amazon' && !isCsvImport;
            } else if (lowerConfig === 'shopify_csv') {
              return connectionType === 'shopify' && isCsvImport;
            } else if (lowerConfig === 'amazon_csv') {
              return connectionType === 'amazon' && isCsvImport;
            }
            return false;
          }

          return true;
        })
        .map((connection: any) => {
          const type = connection.type?.toLowerCase() || '';
          const service = connection.service?.toLowerCase() || '';
          let connectionType = '';
          let isCsvImport = false;

          if (type.includes('shopify')) {
            connectionType = 'shopify';
          } else if (type.includes('amazon')) {
            connectionType = 'amazon';
          } else if ((service === 'csv' || type === 'csv') &&
            connection.import_type === 'ecommerce' &&
            connection.ecommerce_subtype) {
            const subtype = connection.ecommerce_subtype.toLowerCase();
            isCsvImport = true;
            if (subtype === 'shopify') connectionType = 'shopify';
            else if (subtype === 'amazon') connectionType = 'amazon';
          }

          if (!connectionType) return null;

          const connectionId = connection.id || connection.connectionId;
          const displayConnectionType = connectionType.charAt(0).toUpperCase() + connectionType.slice(1);
          let displayName = connection.name || displayConnectionType;

          // Track the source type for API calls (e.g., 'amazon', 'shopify', 'amazon_csv', 'shopify_csv')
          const sourceType = isCsvImport ? `${connectionType}_csv` : connectionType;

          if (isCsvImport) {
            displayName = `${displayConnectionType} CSV Import`;
          }

          return {
            id: connectionId,
            name: displayName,
            sourceType
          };
        })
        .filter(Boolean) as Array<{ id: string; name: string; sourceType: string }>;

      sources.push(...supportedConnections);
    }

    return sources;
  };

  // Update filename when data source changes
  useEffect(() => {
    if (exportSettings.dataSource) {
      let sourceName = 'all_sources';

      if (exportSettings.dataSource !== 'all') {
        const source = getAvailableDataSources().find(s => s.id === exportSettings.dataSource);
        sourceName = source?.name.replace(/\s+/g, '_').toLowerCase() || 'export';
      }

      const date = new Date().toISOString().split('T')[0];

      let reportName = '';
      if (context === 'inventory') {
        reportName = inventoryActiveTab === 'trends' ? 'trends' : 'current_stock';
      } else {
        reportName = reportsActiveTab?.replace('-', '_') || 'report';
      }

      setExportSettings(prev => ({
        ...prev,
        filename: `${sourceName}_${reportName}_${date}.${prev.format}`
      }));
    }
  }, [exportSettings.dataSource, exportSettings.format, context, inventoryActiveTab, reportsActiveTab]);

  const handleExport = async () => {
    if (!exportSettings.dataSource) {
      toast.error('Please select a data source');
      return;
    }

    setIsExporting(true);

    try {
      if (context === 'inventory') {
        // Export inventory data directly
        const viewMode = inventoryActiveTab === 'trends' ? 'timeseries' : 'snapshot';

        // Find the source type for the selected data source (for API compatibility)
        const selectedSource = getAvailableDataSources().find(s => s.id === exportSettings.dataSource);
        const sourceTypeForApi = selectedSource?.sourceType || 'all';

        const payload = {
          organisationId: organisation.id,
          reportType: 'inventory',
          filters: {
            // Use source type (e.g., 'amazon', 'shopify') instead of connection ID
            dataSource: sourceTypeForApi === 'all' ? 'All' : sourceTypeForApi,
            startDate: inventoryFilters?.customStartDate,
            endDate: inventoryFilters?.customEndDate,
            skus: inventoryActiveTab === 'trends' ? inventoryFilters?.selectedSkus : undefined,
            ...inventoryFilters?.multiColumnFilters?.reduce((acc, f) => {
              acc[f.column] = f.value;
              return acc;
            }, {} as any),
            viewMode,
          },
          dateRange: inventoryFilters?.dateRange || 'all_time',
          pageSize: 100000, // Fetch all rows
          page: 1,
          sortColumn: inventoryFilters?.sortColumn,
          sortDirection: inventoryFilters?.sortDirection
        };

        const { data, error } = await invokeFunction(supabase, 'inventory-data-v6', {
          body: payload
        });

        if (error) throw error;

        if (!data || !data.data || data.data.length === 0) {
          toast.error('No data available to export');
          setIsExporting(false);
          return;
        }

        // Convert to CSV and download
        const csvContent = convertToCSV(data.data, exportSettings.includeHeaders);
        downloadFile(csvContent, exportSettings.filename);
        toast.success(`Exported ${data.data.length} rows successfully`);
        onClose();
      } else {
        // Use background export for reports (handled by background-report-export)
        const exportFilters = exportSettings.dataSource === 'all'
          ? { ...reportsFilters, trailing12Months: true }
          : { ...reportsFilters, dataSource: exportSettings.dataSource, trailing12Months: true };

        const result = await startBackgroundExport({
          connectionId: `report-${reportsActiveTab}`,
          schema: 'reports',
          tableName: reportsActiveTab || 'skus',
          filters: [],
          customSQL: JSON.stringify({
            reportType: reportsActiveTab,
            organisationId: organisation.id,
            filters: exportFilters
          })
        });

        if (result.success) {
          toast.success(`Export started. You'll be notified when ready.`);
          onClose();
        } else {
          throw new Error(result.error || 'Failed to start background export');
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data: any[], includeHeaders: boolean): string => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map(row => {
      return headers.map(header => {
        let value = row[header];

        // Handle price_set objects
        if (header === 'price_set' && value && typeof value === 'object') {
          return JSON.stringify(value);
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return `"${value.join(', ')}"`;
        }

        // Handle objects
        if (value && typeof value === 'object') {
          return JSON.stringify(value);
        }

        // Handle null/undefined
        if (value === null || value === undefined) {
          return '';
        }

        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      }).join(',');
    });

    if (includeHeaders) {
      return [headers.join(','), ...rows].join('\n');
    }

    return rows.join('\n');
  };

  if (!isOpen) return null;

  const sources = getAvailableDataSources();

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[480px]">
      <Card className="p-6 shadow-lg border-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{getExportTitle()}</h3>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isExporting}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4">
          {/* Data Source Selection */}
          <div className="space-y-2">
            <Label htmlFor="dataSource">Data Source</Label>
            <Select
              value={exportSettings.dataSource}
              onValueChange={(value) => setExportSettings({ ...exportSettings, dataSource: value })}
              disabled={isExporting}
            >
              <SelectTrigger id="dataSource">
                <SelectValue placeholder="Select a data source" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Format Selection */}
          <div className="space-y-2">
            <Label htmlFor="format">Export Format</Label>
            <Select
              value={exportSettings.format}
              onValueChange={(value) => setExportSettings({ ...exportSettings, format: value })}
              disabled={isExporting}
            >
              <SelectTrigger id="format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Filename */}
          <div className="space-y-2">
            <Label htmlFor="filename">Filename</Label>
            <Input
              id="filename"
              value={exportSettings.filename}
              onChange={(e) => setExportSettings({ ...exportSettings, filename: e.target.value })}
              disabled={isExporting}
            />
          </div>

          {/* Include Headers */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeHeaders"
              checked={exportSettings.includeHeaders}
              onCheckedChange={(checked) =>
                setExportSettings({ ...exportSettings, includeHeaders: checked as boolean })
              }
              disabled={isExporting}
            />
            <Label htmlFor="includeHeaders" className="font-normal cursor-pointer">
              Include column headers
            </Label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isExporting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting || !exportSettings.dataSource}
              className="flex-1"
            >
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export Report
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
